import { useState, useCallback, useEffect } from "react";

const storageName = "userData";

function tokenExpiresAtMs(jwtToken) {
  if (!jwtToken || typeof jwtToken !== "string") return null;
  const parts = jwtToken.split(".");
  if (parts.length < 2) return null;

  try {
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const pad = base64.length % 4 === 0 ? "" : "=".repeat(4 - (base64.length % 4));
    const payload = JSON.parse(atob(`${base64}${pad}`));
    const exp = Number(payload?.exp);
    return Number.isFinite(exp) ? exp * 1000 : null;
  } catch (_) {
    return null;
  }
}

function isTokenUsable(jwtToken) {
  const expiresAt = tokenExpiresAtMs(jwtToken);
  if (expiresAt == null) return Boolean(jwtToken);
  return expiresAt > Date.now();
}

const useAuth = () => {
  const [token, setToken] = useState(null);
  const [ready, setReady] = useState(false);

  const login = useCallback((jwtToken) => {
    setToken(jwtToken);
    localStorage.setItem(storageName, JSON.stringify({ token: jwtToken }));
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    localStorage.removeItem(storageName);
  }, []);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(storageName);
      const data = raw ? JSON.parse(raw) : null;
      if (data?.token && isTokenUsable(data.token)) {
        login(data.token);
      } else if (data?.token) {
        localStorage.removeItem(storageName);
      }
    } catch (_) {
      // Private WebViews can block localStorage; continue without a cached token.
    }
    setReady(true);
  }, [login])

  useEffect(() => {
    if (!token) return undefined;

    const logoutIfExpired = () => {
      const expiresAt = tokenExpiresAtMs(token);
      if (expiresAt != null && expiresAt <= Date.now()) logout();
    };

    logoutIfExpired();

    const expiresAt = tokenExpiresAtMs(token);
    const delay = expiresAt == null ? null : expiresAt - Date.now();
    const timerId = delay != null && delay > 0 ? window.setTimeout(logout, delay) : null;

    window.addEventListener("focus", logoutIfExpired);
    document.addEventListener("visibilitychange", logoutIfExpired);

    return () => {
      if (timerId != null) window.clearTimeout(timerId);
      window.removeEventListener("focus", logoutIfExpired);
      document.removeEventListener("visibilitychange", logoutIfExpired);
    };
  }, [token, logout]);

  return { login, logout, token, ready };
}

export default useAuth;